import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { animate, motion, MotionConfig, useMotionValue, useMotionValueEvent, useTransform } from 'motion/react';
import { ChevronLeft } from 'lucide-react';
import { webModules } from '@nw/module-registry/web';
import { ModulesProvider, WindowProvider, api, useFormat, useHome, useMe, usePreferences, useReducedMotionPreference, useUpdatePreferences, type AssistantCommand, type WebModule, type WindowApi, type WindowTool } from '@nw/web-platform';
import { useAtmosphere } from './atmosphere.js';
import { Scene, useSceneFor } from './Scene.js';
import { allSpaces, pagerSpaces, spaceIdFor, useSpaceMemory, type Space } from './spaces.js';
import { Scrubber } from './Scrubber.js';
import { AskOrb, SpaceActions } from './ThumbControls.js';
import { SpacesGrid } from './SpacesGrid.js';
import { AskSheet } from './AskSheet.js';
import { YouSheet, useNotifications } from './You.js';
import { QuickCapture } from './QuickCapture.js';
import { usePinch } from './gestures.js';
import { coreCommands } from './assistant/core-commands.js';

/** Pending projections drive the "updating" state and refresh views when they settle. */
function usePendingProjections() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['pending-projections'],
    queryFn: async () => (await api<{ outbox: { pending: number } }>('/diagnostics')).data.outbox.pending,
    refetchInterval: (query) => ((query.state.data ?? 0) > 0 ? 1200 : 20_000),
  });
  const last = useRef(0);
  useEffect(() => {
    const p = q.data ?? 0;
    if (last.current > 0 && p === 0) {
      for (const key of ['graph', 'home', 'entity', 'search']) void qc.invalidateQueries({ queryKey: [key] });
    }
    last.current = p;
  }, [q.data, qc]);
  return q.data ?? 0;
}

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 20_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const typing = (el: EventTarget | null) => el instanceof HTMLElement && !!el.closest('input,textarea,select,[contenteditable="true"]');
const CONTROLS = 'a,button,input,textarea,select,label,[role="slider"],[role="button"],[data-no-swipe]';
const interactive = (el: EventTarget | null) => el instanceof Element && !!el.closest(`${CONTROLS},.nw-widget`);
const control = (el: EventTarget | null) => el instanceof Element && !!el.closest(CONTROLS);

type ResumeData = {
  book: { id: string; title: string };
  currentPage: number | null;
};

/** What is happening right now, in one line, floating at the top. */
function LivePill({ pending, onYou }: { pending: number; onYou: () => void }) {
  const home = useHome();
  const navigate = useNavigate();
  const notices = useNotifications(pending);
  const resume = home.data?.sections.find((s) => s.moduleId === 'library' && s.kind === 'resume')?.data as ResumeData | null | undefined;
  if (pending > 0)
    return (
      <span role="status" className="nw-live-pill">
        <span className="nw-live-dot nw-live-dot-pulse" /> Updating connections: {pending}
      </span>
    );
  if (notices.length)
    return (
      <button type="button" className="nw-live-pill nw-live-pill-alert" onClick={onYou}>
        <span className="nw-live-dot nw-live-dot-alert" /> {notices[0]!.title}
      </button>
    );
  if (resume)
    return (
      <button
        type="button"
        className="nw-live-pill"
        onClick={() =>
          void navigate({
            to: '/library/books/$bookId/read',
            params: { bookId: resume.book.id },
          })
        }
        aria-label={`Continue reading ${resume.book.title}`}
      >
        <span className="nw-live-dot" />
        <span className="max-w-[40vw] truncate">{resume.book.title}</span>
        {resume.currentPage ? <span className="opacity-60">p.{resume.currentPage}</span> : null}
      </button>
    );
  return null;
}

export function Shell() {
  const me = useMe();
  const prefs = usePreferences();
  const updatePrefs = useUpdatePreferences();
  const navigate = useNavigate();
  const reduced = useReducedMotionPreference();
  const location = useRouterState({ select: (s) => s.location });
  const pending = usePendingProjections();
  const atmosphere = useAtmosphere();
  const fmt = useFormat();
  const now = useNow();

  const enabled = useMemo<WebModule[]>(() => webModules.filter((m) => me.data?.enabledModules.includes(m.id)), [me.data?.enabledModules]);
  const spaces = useMemo(() => allSpaces(enabled), [enabled]);
  const pager = useMemo(() => pagerSpaces(spaces, prefs.data?.dockModules), [spaces, prefs.data?.dockModules]);
  const spaceId = spaceIdFor(location.pathname, enabled);
  const space = spaces.find((s) => s.id === spaceId) ?? null;
  const scene = useSceneFor(space, atmosphere);

  // ---- where you were in each space ----------------------------------------------------------
  const { last, forWorkspace, remember } = useSpaceMemory();
  useEffect(() => {
    if (me.data) forWorkspace(me.data.workspace.id);
  }, [me.data, forWorkspace]);
  useEffect(() => {
    if (spaceId) remember(spaceId, location.href);
  }, [spaceId, location.href, remember]);
  const go = useCallback(
    (s: Space) => {
      const href = s.id === spaceId ? s.path : (last[s.id] ?? s.path);
      void navigate({ to: href });
    },
    [last, navigate, spaceId],
  );

  // ---- overlays ---------------------------------------------------------------------------------
  const [ask, setAsk] = useState<{ open: boolean; listen: boolean }>({
    open: false,
    listen: false,
  });
  const [gridOpen, setGridOpen] = useState(false);
  const [youOpen, setYouOpen] = useState(false);
  const [capture, setCapture] = useState<{
    open: boolean;
    initial: string | null;
  }>({ open: false, initial: null });
  const quickActions = useMemo(() => enabled.flatMap((m) => m.quickActions ?? []), [enabled]);

  // ---- the space's tools (under the right thumb) ------------------------------------------------
  const [tools, setTools] = useState<WindowTool[]>([]);
  const [portal, setPortal] = useState<HTMLElement | null>(null);
  const windowApi = useMemo<WindowApi>(() => ({ pane: 'primary', setMeta: () => undefined, setTools, portal }), [portal]);

  // ---- theme follows the scene: sheets and menus match the space you are in ----------------------
  useEffect(() => {
    document.documentElement.dataset.theme = scene.mode;
    document.documentElement.dataset.tone = scene.mode;
  }, [scene.mode]);

  // ---- the pager: the space follows your finger ---------------------------------------------------
  const x = useMotionValue(0);
  const width = () => window.innerWidth;
  const index = spaceId ? pager.findIndex((s) => s.id === spaceId) : -1;
  const neighbour = (dir: 1 | -1) => (index >= 0 ? pager[index + dir] : undefined);
  const [peek, setPeek] = useState<Space | null>(null);
  useMotionValueEvent(x, 'change', (v) => {
    const n = v < -4 ? neighbour(1) : v > 4 ? neighbour(-1) : undefined;
    setPeek((p) => (p?.id === n?.id ? p : (n ?? null)));
  });
  const contentScale = useTransform(x, (v) => 1 - Math.min(0.08, (Math.abs(v) / Math.max(1, width())) * 0.12));
  const contentOpacity = useTransform(x, (v) => 1 - Math.min(0.6, (Math.abs(v) / Math.max(1, width())) * 0.9));
  const peekOpacity = useTransform(x, (v) => Math.min(1, (Math.abs(v) / Math.max(1, width())) * 1.6));
  const peekX = useTransform(x, (v) => (v < 0 ? width() + v : v - width()) * 0.35);
  const sceneShift = useTransform(x, (v) => v * 0.12);

  const track = useRef<{
    key: string;
    x0: number;
    y0: number;
    axis: 'x' | 'y' | null;
    samples: Array<{ t: number; x: number }>;
  } | null>(null);
  const begin = (key: string, cx: number, cy: number, axis: 'x' | null = null) => {
    track.current = {
      key,
      x0: cx,
      y0: cy,
      axis,
      samples: [{ t: performance.now(), x: cx }],
    };
  };
  const moveTo = (cx: number, cy: number) => {
    const t = track.current;
    if (!t) return;
    const dx = cx - t.x0;
    const dy = cy - t.y0;
    if (!t.axis) {
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.2) t.axis = 'x';
      else if (Math.abs(dy) > 12) {
        track.current = null;
        return;
      } else return;
    }
    t.samples.push({ t: performance.now(), x: cx });
    if (t.samples.length > 6) t.samples.shift();
    const dir: 1 | -1 = dx < 0 ? 1 : -1;
    x.set(neighbour(dir) ? dx : dx * 0.22);
  };
  const finish = () => {
    const t = track.current;
    track.current = null;
    if (!t || t.axis !== 'x')
      return void animate(x, 0, {
        type: 'spring',
        stiffness: 420,
        damping: 38,
      });
    const first = t.samples[0]!;
    const lastS = t.samples[t.samples.length - 1]!;
    const v = (lastS.x - first.x) / Math.max(1, lastS.t - first.t);
    const dx = x.get();
    const dir: 1 | -1 = dx < 0 ? 1 : -1;
    const target = neighbour(dir);
    if (target && (Math.abs(dx) > width() * 0.2 || Math.abs(v) > 0.5)) {
      void animate(x, -dir * width(), reduced ? { duration: 0 } : { duration: 0.22, ease: [0.4, 0, 1, 1] }).then(() => {
        go(target);
        x.set(dir * width() * 0.3);
        void animate(x, 0, reduced ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 34 });
      });
    } else void animate(x, 0, { type: 'spring', stiffness: 420, damping: 38 });
  };
  const stepSpace = (dir: 1 | -1) => {
    const target = neighbour(dir) ?? (index < 0 ? pager[0] : undefined);
    if (!target) return;
    x.set(dir * width() * 0.3);
    go(target);
    void animate(x, 0, reduced ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 34 });
  };
  const edge = (side: 'left' | 'right') => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      begin(`edge-${side}`, e.clientX, e.clientY, 'x');
    },
    onPointerMove: (e: React.PointerEvent) => moveTo(e.clientX, e.clientY),
    onPointerUp: finish,
    onPointerCancel: finish,
  });

  // Two fingers anywhere swipe between spaces; one finger does on Home's open surface.
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const twoStart = useRef<{ d: number } | null>(null);
  const centroid = () => {
    const pts = [...touches.current.values()];
    return {
      x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
      y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
    };
  };
  const spread = () => {
    const [a, b] = [...touches.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };
  const pinch = usePinch(() => spaceId === 'home' && setGridOpen(true));
  const surface = {
    onPointerDownCapture: (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') {
        touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (touches.current.size === 2) {
          const c = centroid();
          twoStart.current = { d: spread() };
          begin('two', c.x, c.y);
        }
      }
      if (spaceId === 'home') pinch.onPointerDown(e);
    },
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button > 0 || track.current || !(e.target instanceof Element)) return;
      // Home's whole surface swipes. On touch, a quick swipe over a widget does too: a widget
      // only lifts for moving after a still hold, so the two never compete.
      if (spaceId === 'home' && e.pointerType === 'touch' ? control(e.target) : !e.target.closest('[data-space-swipe]') || interactive(e.target)) return;
      begin('one', e.clientX, e.clientY);
    },
    onPointerMoveCapture: (e: React.PointerEvent) => {
      if (touches.current.has(e.pointerId)) touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (spaceId === 'home') pinch.onPointerMove(e);
      const t = track.current;
      if (!t) return;
      if (t.key === 'two' && touches.current.size === 2) {
        // A pinch is not a swipe: bail out if the fingers spread or close.
        if (twoStart.current && Math.abs(spread() - twoStart.current.d) > 60 && t.axis !== 'x') return void (track.current = null);
        const c = centroid();
        moveTo(c.x, c.y);
      } else if (t.key === 'one') moveTo(e.clientX, e.clientY);
    },
    onPointerUpCapture: (e: React.PointerEvent) => {
      touches.current.delete(e.pointerId);
      if (spaceId === 'home') pinch.onPointerUp(e);
      const t = track.current;
      if (t && (t.key === 'one' || (t.key === 'two' && touches.current.size < 2))) finish();
    },
    onPointerCancelCapture: (e: React.PointerEvent) => {
      touches.current.delete(e.pointerId);
      if (track.current) finish();
    },
    onWheel: spaceId === 'home' ? pinch.onWheel : undefined,
  };

  // Pull down from the top edge: Ask.
  const pull = useRef<{ y0: number } | null>(null);
  const topEdge = {
    onPointerDown: (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      pull.current = { y0: e.clientY };
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (pull.current && e.clientY - pull.current.y0 > 70) {
        pull.current = null;
        setAsk({ open: true, listen: false });
      }
    },
    onPointerUp: () => (pull.current = null),
  };

  // ---- commands ------------------------------------------------------------------------------------
  const assistantCtx = useMemo(() => ({ navigate: (to: string) => void navigate({ to }) }), [navigate]);
  const commands = useMemo<AssistantCommand[]>(
    () => [
      ...enabled.flatMap((m) => m.commands ?? []),
      ...coreCommands({
        modules: enabled,
        openSpaces: () => setGridOpen(true),
        setAtmosphere: async (a) => void (await updatePrefs.mutateAsync({ atmosphere: a })),
      }),
    ],
    [enabled, updatePrefs],
  );

  // ---- keyboard -------------------------------------------------------------------------------------
  const toolsRef = useRef(tools);
  toolsRef.current = tools;
  const stepRef = useRef(stepSpace);
  stepRef.current = stepSpace;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault();
        setAsk((a) => ({ open: !a.open, listen: false }));
        return;
      }
      if ((e.metaKey || e.ctrlKey) && k === 'j') {
        e.preventDefault();
        setGridOpen((o) => !o);
        return;
      }
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === '/') {
        e.preventDefault();
        setAsk({ open: true, listen: false });
      } else if (e.key === ']') stepRef.current(1);
      else if (e.key === '[') stepRef.current(-1);
      else {
        const tool = toolsRef.current.find((t) => t.shortcut && t.shortcut.toLowerCase() === k);
        if (tool) {
          e.preventDefault();
          tool.run();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const signOut = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    window.location.assign('/signin');
  };
  const peekScene = useSceneFor(peek, atmosphere);
  const PeekIcon = peek?.icon;
  const name = me.data?.user.displayName ?? '';

  return (
    <ModulesProvider value={enabled}>
      <MotionConfig reducedMotion={reduced ? 'always' : 'never'}>
        <div className="nw-os" data-tone={scene.mode}>
          <motion.div className="nw-os-scene" style={{ x: sceneShift }}>
            <Scene key={scene.key} spec={scene} className="nw-scene-in" />
          </motion.div>
          {peek ? (
            <motion.div className="nw-os-scene" style={{ opacity: peekOpacity }}>
              <Scene spec={peekScene} />
            </motion.div>
          ) : null}
          {peek && PeekIcon ? (
            <motion.div className="nw-peek" style={{ x: peekX, opacity: peekOpacity }} data-tone={peekScene.mode} aria-hidden>
              <span className="nw-peek-icon" style={{ ['--h' as string]: peek.hue }}>
                <PeekIcon className="size-9" />
              </span>
              <span className="nw-display text-[2.6rem]">{peek.label}</span>
            </motion.div>
          ) : null}

          <a href="#window" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-full focus:bg-[var(--nw-surface)] focus:px-4 focus:py-2">
            Skip to content
          </a>

          <motion.main
            id="window"
            ref={setPortal}
            className="nw-space"
            data-space={spaceId ?? 'system'}
            data-tone={scene.mode}
            aria-label={space ? `${space.label} space` : 'Page'}
            style={{ x, scale: contentScale, opacity: contentOpacity }}
            {...surface}
          >
            <div className="nw-space-scroll">
              <WindowProvider value={windowApi}>
                <Outlet />
              </WindowProvider>
            </div>
          </motion.main>

          {/* Gesture edges: swipe in from a side to change space, pull down from the top to ask. */}
          <div className="nw-edge nw-edge-left" aria-hidden {...edge('left')} />
          <div className="nw-edge nw-edge-right" aria-hidden {...edge('right')} />
          <div className="nw-edge-top" aria-hidden {...topEdge} />

          <header className="nw-status" data-tone={scene.mode}>
            {space ? (
              <div className="nw-status-time">
                <span className="nw-display text-[1.35rem] leading-none">{fmt.time(now)}</span>
                <span className="text-[12px] opacity-65">{fmt.today(now)}</span>
              </div>
            ) : (
              <button type="button" className="nw-back" onClick={() => (window.history.length > 1 ? window.history.back() : void navigate({ to: '/' }))}>
                <ChevronLeft className="size-5" /> Back
              </button>
            )}
            <div className="nw-status-mid">
              {me.data?.workspace.isDemo ? <span className="nw-demo-note">Demo workspace: sample content, kept apart from any real account</span> : null}
              <LivePill pending={pending} onYou={() => setYouOpen(true)} />
            </div>
            <button type="button" className="nw-you" onClick={() => setYouOpen(true)} aria-label={`You: ${name}`}>
              {name.slice(0, 1).toUpperCase() || '?'}
            </button>
          </header>

          <nav className="nw-thumbs" aria-label="Spaces" data-tone={scene.mode}>
            <AskOrb onAsk={(listen) => setAsk({ open: true, listen })} />
            <Scrubber spaces={pager} currentId={spaceId} onGo={go} onOpenGrid={() => setGridOpen(true)} />
            <SpaceActions tools={tools} />
          </nav>
        </div>

        <SpacesGrid open={gridOpen} onOpenChange={setGridOpen} spaces={spaces} currentId={spaceId} onGo={go} />
        <AskSheet
          open={ask.open}
          listen={ask.listen}
          onOpenChange={(o) => setAsk({ open: o, listen: false })}
          commands={commands}
          ctx={assistantCtx}
          actions={quickActions}
          spaces={pager}
          onCapture={(id) => setCapture({ open: true, initial: id })}
          onGo={go}
        />
        <YouSheet open={youOpen} onOpenChange={setYouOpen} pending={pending} onSignOut={() => void signOut()} />
        <QuickCapture open={capture.open} initial={capture.initial} onOpenChange={(o) => setCapture((c) => ({ ...c, open: o }))} actions={quickActions} />
      </MotionConfig>
    </ModulesProvider>
  );
}
